import React from "react"
import { useGetHasCarrotPartner } from "../../hooks/useGetCarrotPartner"
import { Redirect } from "react-router"
import { providerFinderRootPath } from "#/pages/provider-finder/utils/providerFinderSearchUrl"
import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { Box, Typography, useTheme, useMediaQuery, Divider } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { SidebarLocationPicker } from "./SidebarLocationPicker"
import { RadiusSelect } from "./RadiusSelect"
import { TierInfoWhenCustomNetworksForSidebar } from "./TierInfoMessage"
import { CarrotPartnerFilter } from "./CarrotPartnerFilter"
import { ServiceFormatFilter } from "./ServiceFormatFilter"
import { ProviderTypeFilter } from "./ProviderTypeFilter"
import {
  FertilityWraparoundMessageForSidebar,
  FertilityWraparoundMessageWhenAdditionalEmployerBenefitsForSidebar
} from "./FertilityWraparoundMessage"
import { useMapViewContext } from "../../context/MapViewContext"
import { SidebarKeywordSearch } from "./SidebarKeywordSearch"
import useGetCustomNetworks from "#/pages/provider-finder/hooks/useGetCustomNetworks"
import { Loading } from "../Loading"
import { ProviderNetworksFilter } from "./ProviderNetworkFilter"
import { useProviderTypeSearched } from "../results/useProviderTypeSearched"
import { DoulaServicesFilter } from "#/pages/provider-finder/components/sidebar/DoulaServicesFilter"
import { useUserContext } from "#/pages/provider-finder/context/UserContext"
import { HasCustomNetworksResponse } from "#/pages/provider-finder/types/HasCustomNetworksResponse"
import { HasCarrotPartnerResponse } from "#/pages/provider-finder/types/HasCarrotPartnerResponse"
import { CarrotCenterOfExcellenceFilter } from "#/pages/provider-finder/components/sidebar/CarrotCenterOfExcellenceFilter"
import { DirectPayFilter } from "#/pages/provider-finder/components/sidebar/DirectPayFilter"
import { useGetProviderFinderNetworkCustomization } from "#/components/views/app/navigation/hooks/useGetProviderFinderNetworkCustomization"
import { ProviderFinderNetworkCustomization } from "#/lib/carrot-api/types/ProviderFinderNetworkCustomization"

function useProviderInfo() {
  const { companyCountryCode } = useUserContext()
  const hasCarrotPartnerQuery = useGetHasCarrotPartner(companyCountryCode)
  const customNetworksQuery = useGetCustomNetworks()
  const providerFinderNetworkCustomizationQuery = useGetProviderFinderNetworkCustomization()

  const isLoading =
    hasCarrotPartnerQuery.isLoading ||
    customNetworksQuery.isLoading ||
    providerFinderNetworkCustomizationQuery.isLoading
  const isError =
    hasCarrotPartnerQuery.isError || customNetworksQuery.isError || providerFinderNetworkCustomizationQuery.isError

  return {
    isLoading,
    isError,
    hasCarrotPartnerData: hasCarrotPartnerQuery.data,
    customNetworksData: customNetworksQuery.data,
    providerFinderNetworkCustomization: providerFinderNetworkCustomizationQuery.data
  }
}

function SideBarFilterKeywordSearch({
  benefit,
  customNetworksData
}: {
  benefit: Benefit
  customNetworksData: HasCustomNetworksResponse
}): JSX.Element {
  const intl = useIntl()
  const isCarrotCore = benefit.program.isCarrotLite
  const isCustomNetworks = customNetworksData?.hasCustomNetworks
  const { isDoulasSearched } = useProviderTypeSearched()
  const searchLabel = isDoulasSearched
    ? intl.formatMessage({ defaultMessage: "Doula's name" })
    : intl.formatMessage({ defaultMessage: "Practice name" })
  return !isCarrotCore || isCustomNetworks ? (
    <Box display="flex" flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}>
      <Typography variant="h6" paddingLeft={(theme) => theme.spacing(theme.tokens.spacing.md)}>
        {searchLabel}
      </Typography>
      <SidebarKeywordSearch />
    </Box>
  ) : null
}

function DistanceFilter(): JSX.Element {
  const { showMapView } = useMapViewContext()
  return !showMapView ? (
    <Box display="flex" flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}>
      <Typography variant="h6" paddingLeft={(theme) => theme.spacing(theme.tokens.spacing.md)}>
        <FormattedMessage defaultMessage="Distance" />
      </Typography>
      <RadiusSelect />
    </Box>
  ) : null
}

function ServiceFormatFilterSection(): JSX.Element {
  const { isDoulasSearched } = useProviderTypeSearched()
  return !isDoulasSearched ? (
    <Box display="flex" flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}>
      <Typography variant="h6" paddingLeft={(theme) => theme.spacing(theme.tokens.spacing.md)}>
        <FormattedMessage defaultMessage="Service format" />
      </Typography>
      <ServiceFormatFilter />
    </Box>
  ) : null
}

function CheckboxFilters({
  hasCarrotPartnerData,
  providerFinderNetworkCustomization
}: {
  hasCarrotPartnerData: HasCarrotPartnerResponse
  providerFinderNetworkCustomization: ProviderFinderNetworkCustomization
}) {
  const theme = useTheme()
  const showFilterDivider = useMediaQuery(theme.breakpoints.down("lg"))
  const { isDoulasSearched } = useProviderTypeSearched()
  const showCarrotPartnerFilter = hasCarrotPartnerData?.hasCarrotPartner && !isDoulasSearched
  const externalProviderNetworkId = providerFinderNetworkCustomization?.externalProviderNetworkId
  const hasFiltersToShow = showCarrotPartnerFilter || externalProviderNetworkId
  return hasFiltersToShow ? (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        backgroundColor: theme.palette.background.navigation
      }}
      borderRadius={(theme) => theme.tokens.borderRadius.md}
      padding={(theme) => theme.spacing(theme.tokens.spacing.md)}
      gap={(theme) => theme.spacing(theme.tokens.spacing.xl)}
    >
      {showFilterDivider && <Divider />}
      <Box
        display="flex"
        flexDirection="column"
        gap={(theme) => theme.spacing(theme.tokens.spacing.xxxs)}
        sx={{
          backgroundColor: theme.palette.background.paper
        }}
        paddingTop={(theme) => theme.spacing(theme.tokens.spacing.md)}
        paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.xxs)}
      >
        <Box display="flex" flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
          <Typography variant="h6" paddingLeft={(theme) => theme.spacing(theme.tokens.spacing.md)}>
            <FormattedMessage defaultMessage="Filter by" />
          </Typography>
          <CarrotCenterOfExcellenceFilter />
          {showCarrotPartnerFilter && <CarrotPartnerFilter />}
          <DirectPayFilter />
          <ProviderNetworksFilter
            chipAndFilterText={providerFinderNetworkCustomization?.chipAndFilterText}
            externalProviderNetworkId={externalProviderNetworkId}
          />
        </Box>
      </Box>
    </Box>
  ) : null
}

export function SidebarFilters({ searchLocation, benefit }: { searchLocation: string; benefit: Benefit }): JSX.Element {
  const providerInfo = useProviderInfo()

  if (providerInfo.isLoading) {
    return <Loading />
  }
  if (providerInfo.isError) {
    return <Redirect to={`${providerFinderRootPath}/error`} />
  }

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        sx={(theme) => ({
          backgroundColor: theme.palette.background.navigation,
          borderRadius: theme.tokens.borderRadius.md,
          padding: theme.tokens.spacing.md,
          gap: theme.tokens.spacing.xl
        })}
      >
        <Box display="flex" flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.sm)}>
          <ProviderTypeFilter benefit={benefit} />
          <DoulaServicesFilter />
          <SideBarFilterKeywordSearch benefit={benefit} customNetworksData={providerInfo.customNetworksData} />
          <Box display="flex" flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}>
            <Typography variant="h6" paddingLeft={(theme) => theme.spacing(theme.tokens.spacing.md)}>
              <FormattedMessage defaultMessage="Location" />
            </Typography>
            <SidebarLocationPicker searchLocation={searchLocation} />
          </Box>
          <DistanceFilter />
          <ServiceFormatFilterSection />
        </Box>
      </Box>
      <TierInfoWhenCustomNetworksForSidebar
        showLink={!!benefit.customizations.memberCustomCoverageExplanationMarkdown}
      />

      <CheckboxFilters
        hasCarrotPartnerData={providerInfo.hasCarrotPartnerData}
        providerFinderNetworkCustomization={providerInfo.providerFinderNetworkCustomization}
      />
      {benefit.providerFinder.showFertilityWrapAroundMessage &&
        (benefit.customizations.additionalEmployerBenefitMarkdown ? (
          <FertilityWraparoundMessageWhenAdditionalEmployerBenefitsForSidebar />
        ) : (
          <FertilityWraparoundMessageForSidebar />
        ))}
    </>
  )
}
